import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';
import { analyticsEvents } from '../services/vercelAnalytics';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({ isOpen, onClose }) => {
  const [result, setResult] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track when form opens
  useEffect(() => {
    if (isOpen) {
      analyticsEvents.feedbackFormOpened();
    }
  }, [isOpen]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setResult("Sending...");
    
    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    
    // Add Web3Forms access key
    formData.append("access_key", "82f5439c-504f-45bb-a850-a3a86b5a0095");
    formData.append("subject", "NoMoreAI Feedback");
    formData.append("from_name", "NoMoreAI User");

    try {
      // Direct Web3Forms submission
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json() as { success?: boolean };
      if (data.success) {
        setResult("Thank you for your feedback!");
        
        // Track successful feedback submission
        const name = formData.get("name") as string;
        const email = formData.get("email") as string;
        analyticsEvents.feedbackSubmitted(!!name, !!email);
        
        form.reset();
        setTimeout(() => {
          onClose();
          setResult("");
        }, 2000);
      } else {
        setResult("Error sending feedback. Please try again.");
        analyticsEvents.feedbackFailed();
      }
    } catch (error) {
      setResult("Error sending feedback. Please try again.");
      analyticsEvents.feedbackFailed();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-3xl p-6 w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-[#0A1A2F]">Share Feedback</h3>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-[#475467]" />
              </button>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0A1A2F] mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-[#D0D5DD] rounded-xl focus:ring-2 focus:ring-[#1F4FFF] focus:border-transparent resize-none"
                  placeholder="Tell us about your experience..."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#475467] mb-2">
                    Name (optional)
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="w-full px-3 py-2 border border-[#D0D5DD] rounded-xl focus:ring-2 focus:ring-[#1F4FFF] focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#475467] mb-2">
                    Email (optional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    className="w-full px-3 py-2 border border-[#D0D5DD] rounded-xl focus:ring-2 focus:ring-[#1F4FFF] focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-[#1F4FFF] text-white font-medium rounded-xl hover:bg-[#163ACC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span>{isSubmitting ? 'Sending...' : 'Send Feedback'}</span>
              </button>

              {result && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`text-sm text-center ${
                    result.includes('Thank you') ? 'text-green-600' : 
                    result.includes('Error') ? 'text-red-600' : 'text-[#475467]'
                  }`}
                >
                  {result}
                </motion.p>
              )}
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
